import { consoleRoutes } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Checkbox, Chip, Icon } from "@sagot/ui";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { hasDuplicates } from "./lead-convert-model";
import type { LeadDuplicate, LeadProposal } from "./lead-convert-types";

function DuplicateLine({ duplicate }: { duplicate: LeadDuplicate }) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const href = duplicate.kind === "contact_email" ? consoleRoutes.contacts(summary.slug, duplicate.id) : consoleRoutes.organisations(summary.slug, duplicate.id);
  return (
    <li className="flex items-start gap-2 text-sm" data-testid={`lead-duplicate-${duplicate.kind}`}>
      <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0" />
      <span>
        {t(`service.leadConvert.duplicates.${duplicate.kind}`)}{" "}
        <AppLink href={href} target="_blank" rel="noreferrer" className="font-semibold underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-primary">
          {duplicate.name}
        </AppLink>
      </span>
    </li>
  );
}

interface PreviewProps {
  proposal: LeadProposal;
  organisationName: string;
  link: boolean;
  onLink: (link: boolean) => void;
}

/** Step 2: what the conversion would make, the duplicates it found, and the choice to link to them instead of making new ones. */
export function LeadConvertPreview({ proposal, organisationName, link, onLink }: PreviewProps) {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const duplicated = hasDuplicates(proposal);
  const organisationTaken = proposal.duplicates.some((duplicate) => duplicate.kind !== "contact_email");
  const converted = proposal.converted_to;
  return (
    <div className="space-y-4" data-testid="lead-convert-preview">
      {converted ? (
        <p role="status" className="flex items-start gap-2 rounded-md bg-info-soft px-3 py-2 text-sm text-fg" data-testid="lead-already-converted">
          <Icon name="info" className="mt-0.5 size-4 shrink-0 text-info" />
          <span>
            {t("service.leadConvert.alreadyConverted")}{" "}
            <AppLink href={consoleRoutes.organisations(summary.slug, converted.organisation_id)} className="font-semibold underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-primary">
              {converted.organisation_name}
            </AppLink>
          </span>
        </p>
      ) : null}
      <section aria-labelledby="lead-make-title" className="space-y-2 rounded-md border border-line p-3">
        <h3 id="lead-make-title" className="text-xs font-bold uppercase tracking-wide text-fg-subtle">
          {t("service.leadConvert.preview.title")}
        </h3>
        <ul className="space-y-2 text-sm">
          <li className="flex flex-wrap items-center gap-2" data-testid="lead-will-organisation">
            <Icon name="building-2" className="size-4 shrink-0 text-fg-subtle" />
            <span className="font-semibold">{organisationName}</span>
            <Chip tone={organisationTaken ? (link ? "muted" : "warning") : "success"} icon={organisationTaken ? (link ? "link" : "alert-triangle") : "plus"} data-testid="lead-organisation-chip">
              {organisationTaken ? (link ? t("service.leadConvert.preview.linked") : t("service.leadConvert.preview.taken")) : t("service.leadConvert.preview.new")}
            </Chip>
          </li>
          <li className="flex flex-wrap items-center gap-2" data-testid="lead-will-contact">
            <Icon name="contact" className="size-4 shrink-0 text-fg-subtle" />
            <span className="font-semibold">{proposal.contact_name}</span>
            <span className="text-fg-muted">{[proposal.contact_email, proposal.contact_mobile_masked, proposal.job_title].filter(Boolean).join(" · ")}</span>
          </li>
        </ul>
        <p className="text-xs text-fg-muted">{t("service.leadConvert.preview.linkBack")}</p>
      </section>
      {duplicated ? (
        <section aria-labelledby="lead-dup-title" className="space-y-3 rounded-md border border-warning bg-warning-soft p-3 text-warning" data-testid="lead-duplicates">
          <h3 id="lead-dup-title" className="text-sm font-bold">
            {t("service.leadConvert.duplicates.title", { count: proposal.duplicates.length })}
          </h3>
          <ul className="space-y-1.5">
            {proposal.duplicates.map((duplicate) => (
              <DuplicateLine key={`${duplicate.kind}-${duplicate.id}`} duplicate={duplicate} />
            ))}
          </ul>
          <div className="text-fg">
            <Checkbox checked={link} onCheckedChange={(checked) => { onLink(checked === true); }} label={t("service.leadConvert.duplicates.link")} description={t("service.leadConvert.duplicates.linkHint")} data-testid="lead-link-existing" />
          </div>
        </section>
      ) : (
        <p className="flex items-center gap-2 text-sm text-fg-muted" data-testid="lead-no-duplicates">
          <Icon name="check-circle" className="size-4 shrink-0 text-success" />
          {t("service.leadConvert.duplicates.none")}
        </p>
      )}
    </div>
  );
}
